import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import MarqueeBar from "./MarqueeBar";
import OtpLoginDialog from "./OtpLoginDialog";
import SearchDialog from "./SearchDialog";

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/shop/necklaces", label: "Necklaces" },
  { to: "/shop/earrings", label: "Earrings" },
  { to: "/shop/rings", label: "Rings" },
  { to: "/shop/bracelets", label: "Bracelets" },
  { to: "/blog", label: "Journal" },
];

const shopSub = [
  { to: "/shop", label: "All jewelry" },
  { to: "/shop/necklaces", label: "Necklaces" },
  { to: "/shop/earrings", label: "Earrings" },
  { to: "/shop/rings", label: "Rings" },
  { to: "/shop/bracelets", label: "Bracelets" },
  { to: "/shop/characters/rapunzel", label: "Rapunzel Edit" },
];

const Header = () => {
  const { branding } = useBranding();
  const { customer } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [marquee, setMarquee] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reset submenu state whenever the sheet closes
  useEffect(() => { if (!mobileOpen) setShopOpen(false); }, [mobileOpen]);

  // Load marketing config (marquee text + speed + colors) once
  useEffect(() => {
    api.get("/marketing/config")
      .then((r) => setMarquee(r.data?.marquee || null))
      .catch(() => setMarquee(null));
  }, []);

  const openAccount = () => {
    if (customer) navigate("/account");
    else setLoginOpen(true);
  };

  const fallbackMessage = `Free shipping above ₹${branding.free_shipping_threshold || 1499} · New Bloom Collection now live`;
  const useMarquee = marquee && marquee.enabled && (marquee.messages || []).length > 0;

  return (
    <>
      {useMarquee ? (
        <MarqueeBar
          messages={marquee.messages}
          speed={marquee.speed_seconds}
          bg={marquee.bg_color}
          fg={marquee.text_color}
          testId="top-announcement-bar"
        />
      ) : (
        <div className="w-full bg-charcoal text-cream" data-testid="top-announcement-bar">
          <div className="rieave-container py-2 text-center font-label text-[11px] tracking-[0.28em] uppercase">
            {fallbackMessage}
          </div>
        </div>
      )}
      <header
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          scrolled ? "bg-cream/95 backdrop-blur border-b border-border" : "bg-cream/85 backdrop-blur-sm"
        }`}
        data-testid="site-header"
      >
        <div className="rieave-container flex items-center justify-between py-4">
          <button
            className="lg:hidden text-charcoal"
            aria-label="menu"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu size={22} />
          </button>

          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.brand_name || "rieave"}
                className="h-9 md:h-10 w-auto object-contain"
              />
            ) : (
              <span className="font-hero text-[28px] leading-none tracking-tight text-charcoal">
                {branding.brand_name || "rieave"}
              </span>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `font-label text-[12px] tracking-[0.22em] uppercase transition-colors ${
                    isActive ? "text-rose" : "text-charcoal hover:text-rose"
                  }`
                }
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-4 text-charcoal">
            <button
              className="hidden sm:inline-flex p-2 rounded-full hover:bg-blush/60 transition"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              data-testid="search-button"
            >
              <Search size={18} />
            </button>
            <button
              className="p-2 rounded-full hover:bg-blush/60 transition"
              onClick={openAccount}
              aria-label="Account"
              data-testid="account-button"
            >
              <User size={18} />
            </button>
            <button
              className="hidden sm:inline-flex p-2 rounded-full hover:bg-blush/60 transition"
              onClick={() => (customer ? navigate("/wishlist") : setLoginOpen(true))}
              aria-label="Wishlist"
              data-testid="wishlist-button"
            >
              <Heart size={18} />
            </button>
            <button
              className="relative p-2 rounded-full hover:bg-blush/60 transition"
              onClick={() => setDrawerOpen(true)}
              aria-label="Cart"
              data-testid="cart-button"
            >
              <ShoppingBag size={18} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-rose text-white text-[10px] font-label rounded-full min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center" data-testid="cart-count-badge">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE + TABLET SHEET MENU (left-anchored, matches reference) */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/50"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <motion.aside
              className="fixed top-0 left-0 z-50 h-full w-[min(420px,88vw)] bg-cream flex flex-col overflow-y-auto"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid="mobile-menu"
            >
              {/* header */}
              <div className="flex items-start justify-between px-6 pt-7 pb-6">
                <Link to="/" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-logo">
                  {branding.logo_url ? (
                    <img
                      src={branding.logo_url}
                      alt={branding.brand_name || "rieave"}
                      className="h-10 w-auto object-contain"
                    />
                  ) : (
                    <span className="font-hero text-[40px] leading-none text-charcoal">
                      {branding.brand_name ? branding.brand_name.charAt(0).toUpperCase() + branding.brand_name.slice(1) : "Rieave"}
                    </span>
                  )}
                </Link>
                <button
                  onClick={() => setMobileOpen(false)}
                  aria-label="close menu"
                  className="p-1 -mr-1 text-charcoal/70 hover:text-charcoal"
                  data-testid="mobile-menu-close"
                >
                  <X size={22} strokeWidth={1.5} />
                </button>
              </div>

              {/* search row */}
              <MobileRow
                icon={Search}
                label="Search"
                onClick={() => { setMobileOpen(false); setSearchOpen(true); }}
                testId="mobile-menu-search"
              />

              {/* Shop expandable */}
              <MobileExpand
                label="Shop"
                open={shopOpen}
                onToggle={() => setShopOpen((v) => !v)}
                testId="mobile-menu-shop"
              >
                {shopSub.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    onClick={() => setMobileOpen(false)}
                    className="block pl-12 pr-6 py-3 font-body text-[15px] text-charcoal/75 hover:text-rose border-b border-charcoal/5 last:border-b-0"
                    data-testid={`mobile-menu-shop-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {s.label}
                  </Link>
                ))}
              </MobileExpand>

              <MobileRow label="New In"    to="/shop?tag=new"    onClick={() => setMobileOpen(false)} testId="mobile-menu-newin" />
              <MobileRow label="About"     to="/about"            onClick={() => setMobileOpen(false)} testId="mobile-menu-about" />
              <MobileRow label="Journal"   to="/blog"             onClick={() => setMobileOpen(false)} testId="mobile-menu-journal" />
              <MobileRow label="Contact"   to="/contact"          onClick={() => setMobileOpen(false)} testId="mobile-menu-contact" />

              {/* Spacer to push wishlist / auth to bottom like reference */}
              <div className="flex-1 min-h-[20px]" />

              <MobileRow
                icon={Heart}
                label="Wishlist"
                onClick={() => {
                  setMobileOpen(false);
                  if (customer) navigate("/wishlist"); else setLoginOpen(true);
                }}
                testId="mobile-menu-wishlist"
              />

              {customer ? (
                <MobileRow
                  icon={User}
                  label="My Account"
                  onClick={() => { setMobileOpen(false); navigate("/account"); }}
                  testId="mobile-menu-account"
                />
              ) : (
                <>
                  <MobileRow
                    icon={User}
                    label="Sign In"
                    onClick={() => { setMobileOpen(false); setLoginOpen(true); }}
                    testId="mobile-menu-signin"
                  />
                  <button
                    onClick={() => { setMobileOpen(false); setLoginOpen(true); }}
                    className="w-full text-left px-6 py-5 font-body text-[17px] text-charcoal hover:bg-charcoal/[0.03] transition-colors"
                    data-testid="mobile-menu-createaccount"
                  >
                    Create Account
                  </button>
                </>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <OtpLoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};

const MobileRow = ({ icon: Icon, label, to, onClick, testId }) => {
  const content = (
    <>
      {Icon && <Icon size={19} strokeWidth={1.5} className="text-charcoal/70" />}
      <span className="font-body text-[17px] text-charcoal tracking-tight">{label}</span>
    </>
  );
  const cls = "w-full flex items-center gap-4 px-6 py-5 border-t border-charcoal/10 text-left hover:bg-charcoal/[0.03] transition-colors";
  if (to) {
    return (
      <Link to={to} onClick={onClick} className={cls} data-testid={testId}>
        {content}
      </Link>
    );
  }
  return (
    <button onClick={onClick} className={cls} data-testid={testId}>
      {content}
    </button>
  );
};

const MobileExpand = ({ label, open, onToggle, children, testId }) => (
  <div className="border-t border-charcoal/10">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-6 py-5 hover:bg-charcoal/[0.03] transition-colors"
      data-testid={testId}
      aria-expanded={open}
    >
      <span className="font-body text-[17px] text-charcoal tracking-tight">{label}</span>
      <motion.span animate={{ rotate: open ? 90 : 0 }} transition={{ duration: 0.25 }} className="text-charcoal/50">
        <ChevronRight size={18} strokeWidth={1.5} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden bg-charcoal/[0.02]"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default Header;
